import { useState, useEffect } from 'react';
import { X, FileText, Download, ArrowLeft } from 'lucide-react';
import SignaturePad from './SignaturePad';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import jsPDF from 'jspdf';

export default function QuoteSignatureModal({
  quote,
  customer,
  quoteItems,
  companyId,
  quoteTermsMode = 'optional_on',
  onClose,
  onBack,
  onSigned
}) {
  const { showSuccess, showError } = useModal();
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signing, setSigning] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Track online/offline status changes
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (companyId) {
      loadCompanySettings();
    }
  }, [companyId]);

  const loadCompanySettings = async () => {
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (!error && data) {
        setCompanySettings(data);
      }
    } catch (err) {

    }
  };

  // Helper to convert hex color to RGB array for jsPDF
  const hexToRgb = (hex) => {
    const defaultColor = [255, 107, 53]; // fallback orange
    if (!hex) return defaultColor;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : defaultColor;
  };

  const handleSignature = async (signatureObject) => {
    // Check for internet connectivity - PDF upload and database update require network
    if (!isOnline) {
      showError('Signing requires an internet connection. The signed PDF needs to be uploaded to secure storage. Please connect to the internet and try again.');
      return;
    }

    try {
      setSigning(true);

      const { signatureData, printedName } = signatureObject;

      // Generate PDF with signature
      const pdf = generateQuotePDF(quote, customer, quoteItems, signatureData, printedName, companySettings, quoteTermsMode);

      // Convert to blob
      const pdfBlob = pdf.output('blob');

      // Upload to Supabase Storage
      const fileName = `quote-${quote.quote_number}-signed-${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('quotes')
        .upload(fileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('quotes')
        .getPublicUrl(fileName);

      // Update quote with signed status and PDF URL
      const { error: updateError } = await supabaseData
        .from('quotes')
        .update({
          status: 'signed',
          signed_pdf_url: publicUrl,
          signed_at: new Date().toISOString(),
          signed_by: printedName
        })
        .eq('id', quote.id);

      if (updateError) throw updateError;

      showSuccess('Quote signed successfully!');
      onSigned(publicUrl);
      onClose();
    } catch (error) {

      showError('Failed to sign quote: ' + error.message);
    } finally {
      setSigning(false);
    }
  };

  const generateQuotePDF = (quote, customer, items, signatureData, printedName, settings, termsMode) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    let yPos = margin;

    // Brand Colors from settings (with fallbacks)
    const primaryColor = hexToRgb(settings?.brand_primary_color || '#4ecca3');
    const secondaryColor = hexToRgb(settings?.brand_secondary_color || '#F05A28');
    const textColor = hexToRgb(settings?.brand_text_color || '#333333');
    const darkGray = [60, 60, 60];
    const mediumGray = [120, 120, 120];
    const lightGray = [200, 200, 200];

    // Company name from settings or fallback
    const companyName = settings?.quote_company_name || settings?.name || 'Service Quote';

    // ============ HEADER ============
    // Orange accent bar at top
    pdf.setFillColor(...primaryColor);
    pdf.rect(0, 0, pageWidth, 8, 'F');

    yPos = 20;

    // Company Name (large, left aligned)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(22);
    pdf.setTextColor(...primaryColor);
    pdf.text(companyName, margin, yPos);
    yPos += 8;

    // Company contact info (smaller, below name)
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...mediumGray);

    if (settings?.quote_company_address) {
      pdf.text(settings.quote_company_address, margin, yPos);
      yPos += 4;
    }

    const contactParts = [];
    if (settings?.quote_company_phone) contactParts.push(settings.quote_company_phone);
    if (settings?.quote_company_email) contactParts.push(settings.quote_company_email);
    if (contactParts.length > 0) {
      pdf.text(contactParts.join('  |  '), margin, yPos);
      yPos += 4;
    }

    // Quote number and date (right aligned)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...secondaryColor);
    pdf.text('QUOTE', pageWidth - margin, 20, { align: 'right' });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...textColor);
    pdf.text(`#${quote.quote_number}`, pageWidth - margin, 26, { align: 'right' });

    pdf.setFontSize(9);
    pdf.setTextColor(...mediumGray);
    pdf.text(`Date: ${new Date(quote.created_at).toLocaleDateString()}`, pageWidth - margin, 32, { align: 'right' });

    yPos = Math.max(yPos, 40) + 10;

    // Divider line
    pdf.setDrawColor(...lightGray);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 12;

    // ============ CUSTOMER INFO ============
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...textColor);
    pdf.text('BILL TO:', margin, yPos);
    yPos += 6;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(customer.name || 'Customer', margin, yPos);
    yPos += 5;

    if (customer.address) {
      pdf.setFontSize(9);
      pdf.setTextColor(...mediumGray);
      const addressParts = [customer.address];
      if (customer.city || customer.state || customer.zip) {
        addressParts.push([customer.city, customer.state, customer.zip].filter(Boolean).join(', '));
      }
      addressParts.forEach(line => {
        pdf.text(line, margin, yPos);
        yPos += 4;
      });
    }

    if (customer.phone) {
      pdf.setTextColor(...mediumGray);
      pdf.text(customer.phone, margin, yPos);
      yPos += 4;
    }
    if (customer.email) {
      pdf.text(customer.email, margin, yPos);
      yPos += 4;
    }

    yPos += 10;

    // ============ LINE ITEMS TABLE ============
    // Table header - give more space to price columns
    const colWidths = { desc: contentWidth * 0.46, qty: contentWidth * 0.10, price: contentWidth * 0.22, total: contentWidth * 0.22 };
    const tableX = margin;

    pdf.setFillColor(245, 245, 245);
    pdf.rect(tableX, yPos, contentWidth, 8, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(...textColor);
    pdf.text('Description', tableX + 3, yPos + 5.5);
    pdf.text('Qty', tableX + colWidths.desc + 3, yPos + 5.5);
    pdf.text('Unit Price', tableX + colWidths.desc + colWidths.qty + 3, yPos + 5.5);
    pdf.text('Total', tableX + contentWidth - 3, yPos + 5.5, { align: 'right' });

    yPos += 10;

    // Table rows
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);

    items.forEach((item, index) => {
      // Alternate row background
      if (index % 2 === 1) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(tableX, yPos - 1, contentWidth, 7, 'F');
      }

      pdf.setTextColor(...textColor);
      // Truncate long descriptions
      const desc = (item.name || item.description || 'Item').substring(0, 50);
      pdf.text(desc, tableX + 3, yPos + 4);
      pdf.text(String(item.quantity), tableX + colWidths.desc + 3, yPos + 4);
      pdf.text(`$${item.unit_price.toFixed(2)}`, tableX + colWidths.desc + colWidths.qty + 3, yPos + 4);
      pdf.text(`$${item.total_price.toFixed(2)}`, tableX + contentWidth - 3, yPos + 4, { align: 'right' });

      yPos += 7;
    });

    // Table bottom border
    pdf.setDrawColor(...lightGray);
    pdf.line(tableX, yPos, tableX + contentWidth, yPos);
    yPos += 8;

    // ============ TOTALS ============
    const totalsX = pageWidth - margin - 70;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...mediumGray);
    pdf.text('Subtotal:', totalsX, yPos);
    pdf.setTextColor(...textColor);
    pdf.text(`$${quote.subtotal.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;

    const discountAmount = Number(quote.discount) || 0;
    if (discountAmount > 0) {
      pdf.setTextColor(...mediumGray);
      pdf.text('Discount:', totalsX, yPos);
      pdf.setTextColor(...textColor);
      pdf.text(`-$${discountAmount.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 6;
    }

    if (quote.tax_rate && quote.tax_rate > 0) {
      const taxPercent = (quote.tax_rate * 100).toFixed(1);
      const taxAmount = (quote.subtotal - discountAmount) * quote.tax_rate;
      pdf.setTextColor(...mediumGray);
      pdf.text(`Tax (${taxPercent}%):`, totalsX, yPos);
      pdf.setTextColor(...textColor);
      pdf.text(`$${taxAmount.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 6;
    }

    // Total with background
    pdf.setFillColor(...primaryColor);
    pdf.rect(totalsX - 5, yPos - 1, 75, 9, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text('TOTAL:', totalsX, yPos + 5);
    pdf.text(`$${quote.total.toFixed(2)}`, pageWidth - margin, yPos + 5, { align: 'right' });
    yPos += 18;

    // ============ TERMS & CONDITIONS ============
    // Show terms if: mode is required, OR mode is optional and quote has include_terms=true
    const shouldShowTerms = termsMode === 'required' ||
      (termsMode !== 'disabled' && quote.include_terms !== false);

    if (shouldShowTerms) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...textColor);
      pdf.text('Terms & Conditions', margin, yPos);
      yPos += 6;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...mediumGray);

      // Use custom terms or default
      let termsText = settings?.quote_custom_terms;
      if (!termsText) {
        termsText = 'This quote is valid for 30 days from the date above.\nPayment is due upon completion of work.\nAll work is guaranteed for 1 year from completion date.\nCustomer signature below indicates acceptance of this quote and terms.';
      }

      const termsLines = termsText.split('\n').filter(line => line.trim());
      termsLines.forEach(term => {
        const cleanTerm = term.trim().replace(/^[•\-*]\s*/, '');
        pdf.text(`• ${cleanTerm}`, margin + 2, yPos);
        yPos += 4;
      });

      yPos += 8;
    }

    // ============ SIGNATURE SECTION ============
    pdf.setDrawColor(...lightGray);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...textColor);
    pdf.text('Customer Acceptance', margin, yPos);
    yPos += 8;

    // Signature box
    pdf.setDrawColor(...lightGray);
    pdf.setLineWidth(0.3);
    pdf.rect(margin, yPos, 70, 25);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...mediumGray);
    pdf.text('Signature', margin + 2, yPos + 28);

    // Add signature image if provided
    if (signatureData) {
      try {
        pdf.addImage(signatureData, 'PNG', margin + 2, yPos + 2, 66, 21);
      } catch (error) {

      }
    }

    // Printed name and date
    const sigInfoX = margin + 80;
    pdf.setFontSize(9);
    pdf.setTextColor(...mediumGray);
    pdf.text('Printed Name:', sigInfoX, yPos + 8);
    pdf.setTextColor(...textColor);
    pdf.text(printedName, sigInfoX + 25, yPos + 8);

    pdf.setTextColor(...mediumGray);
    pdf.text('Date:', sigInfoX, yPos + 15);
    pdf.setTextColor(...textColor);
    pdf.text(new Date().toLocaleDateString(), sigInfoX + 25, yPos + 15);

    pdf.setTextColor(...mediumGray);
    pdf.text('Time:', sigInfoX, yPos + 22);
    pdf.setTextColor(...textColor);
    pdf.text(new Date().toLocaleTimeString(), sigInfoX + 25, yPos + 22);

    yPos += 35;

    // ============ CUSTOM FOOTER ============
    if (settings?.quote_custom_footer) {
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...mediumGray);
      const footerLines = pdf.splitTextToSize(settings.quote_custom_footer, contentWidth);
      footerLines.forEach(line => {
        pdf.text(line, pageWidth / 2, yPos, { align: 'center' });
        yPos += 4;
      });
    }

    // ============ FOOTER BAR ============
    pdf.setFillColor(...primaryColor);
    pdf.rect(0, pageHeight - 6, pageWidth, 6, 'F');

    return pdf;
  };

  if (signing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem'
      }}>
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <FileText size={64} style={{ animation: 'pulse 1.5s ease-in-out infinite', margin: '0 auto 1rem' }} />
          <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Processing Signature...</h2>
          <p style={{ color: '#888' }}>Generating signed PDF and saving to secure storage</p>
        </div>
      </div>
    );
  }

  if (showSignaturePad) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '1rem'
      }}>
        <SignaturePad
          onSave={handleSignature}
          onCancel={() => setShowSignaturePad(false)}
          signerName={customer.name}
          signerType="customer"
        />
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '1rem'
    }}>
      <div style={{
        background: '#2a2a2a',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h3 style={{ fontSize: '1.5rem', margin: 0, color: '#F05A28' }}>
              Sign Quote
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ marginBottom: '2rem', color: '#e0e0e0' }}>
          <p style={{ marginBottom: '1rem' }}>
            You're about to sign and accept this quote:
          </p>
          <div style={{
            background: '#1a1a1a',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #3a3a3a'
          }}>
            <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '0.5rem' }}>Quote Number:</div>
            <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#F05A28', marginBottom: '1rem' }}>
              {quote.quote_number}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '0.5rem' }}>Total Amount:</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#4ecca3' }}>
              ${quote.total.toFixed(2)}
            </div>
          </div>
        </div>

        <div style={{
          background: '#1a1a1a',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #3a3a3a',
          marginBottom: '2rem'
        }}>
          <p style={{ fontSize: '0.85rem', color: '#888', margin: 0, lineHeight: '1.6' }}>
            By signing this quote, you agree to the terms and authorize us to proceed with the work as described.
            A signed PDF will be generated and saved for your records.
          </p>
        </div>

        {/* Show offline warning if not connected */}
        {!isOnline && (
          <div style={{
            background: '#F05A2820',
            border: '1px solid #F05A28',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#F05A28',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            <strong>No Internet Connection</strong>
            <p style={{ margin: '0.5rem 0 0 0', color: '#ccc', fontSize: '0.85rem' }}>
              Signing requires internet to upload the signed PDF. Please reconnect and try again.
            </p>
          </div>
        )}

        <button
          onClick={() => {
            if (!isOnline) {
              showError('Signing requires an internet connection. Please connect to the internet and try again.');
              return;
            }
            setShowSignaturePad(true);
          }}
          disabled={!isOnline}
          style={{
            width: '100%',
            padding: '1rem',
            background: isOnline ? '#F05A28' : '#555',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: isOnline ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            opacity: isOnline ? 1 : 0.6
          }}
        >
          <FileText size={20} />
          {isOnline ? 'Sign Quote' : 'Offline - Cannot Sign'}
        </button>
      </div>
    </div>
  );
}
